<template>
  <v-card outlined class="imported-data-panel mt-6">
    <v-card-text>
      <div class="text-subtitle-1 font-weight-medium mb-2">
        {{ $t('deforestation.importedDataTitle') }}
      </div>

      <v-tabs v-model="activeTab" color="primary" height="40" class="mb-3">
        <v-tab>{{ $t('deforestation.importedFarmsTab') }}</v-tab>
        <v-tab>{{ $t('deforestation.uploadHistoryTab') }}</v-tab>
      </v-tabs>

      <!-- Imported farms -->
      <div v-if="activeTab === 0">
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px;">
          <v-text-field
            v-model="farmSearch"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$t('deforestation.importedFarmsSearch')"
            dense hide-details clearable style="max-width: 320px;"
            @input="onFarmSearch"
          />
          <v-spacer />
          <div class="d-flex align-center">
            <span class="text-caption mr-2">{{ farmFrom }} - {{ farmTo }} of {{ farmTotal }}</span>
            <v-btn icon small :disabled="farmPage <= 1" @click="farmPageChange(-1)">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn icon small :disabled="farmPage >= farmTotalPages" @click="farmPageChange(1)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <v-data-table
          :headers="farmHeaders"
          :items="farms"
          :loading="farmLoading"
          :loading-text="$t('loadingData')"
          hide-default-footer
          dense
        >
          <template #item.area="{ item }">
            {{ hectares(item) }}
          </template>
          <template #no-data>
            <div class="py-6 grey--text">{{ $t('deforestation.noImportedFarms') }}</div>
          </template>
        </v-data-table>
      </div>

      <!-- Upload history -->
      <div v-else>
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px;">
          <v-text-field
            v-model="historySearch"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$t('deforestation.uploadHistoryFile')"
            dense hide-details clearable style="max-width: 320px;"
            @input="onHistorySearch"
          />
          <v-spacer />
          <div class="d-flex align-center">
            <span class="text-caption mr-2">{{ historyFrom }} - {{ historyTo }} of {{ historyTotal }}</span>
            <v-btn icon small :disabled="historyPage <= 1" @click="historyPageChange(-1)">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn icon small :disabled="historyPage >= historyTotalPages" @click="historyPageChange(1)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <v-data-table
          :headers="historyHeaders"
          :items="history"
          :loading="historyLoading"
          :loading-text="$t('loadingData')"
          hide-default-footer
          dense
        >
          <template #item.status="{ item }">
            <v-chip x-small :color="statusColor(item.status)" dark>{{ item.status || '-' }}</v-chip>
          </template>
          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template #no-data>
            <div v-if="historyError" class="py-6 error--text">{{ historyError }}</div>
            <div v-else class="py-6 grey--text">{{ $t('deforestation.noUploadHistory') }}</div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { debounce } from 'lodash';
import DeforestationService from '@/_services/DeforestationService';

export default {
  name: 'ImportedDataPanel',
  props: {
    diligenceId: { required: true },
    pageSize: { type: Number, default: 10 },
  },
  data() {
    return {
      activeTab: 0,
      // imported farms
      farms: [],
      farmLoading: false,
      farmSearch: '',
      farmPage: 1,
      farmTotal: 0,
      farmSeq: 0,
      // upload history
      history: [],
      historyLoading: false,
      historySearch: '',
      historyPage: 1,
      historyTotal: 0,
      historySeq: 0,
      historyError: '',
    };
  },
  computed: {
    farmHeaders() {
      return [
        { text: this.$t('deforestation.importedFarmName'), value: 'farmName', sortable: false },
        { text: this.$t('deforestation.importedFarmId'), value: 'farmRef', sortable: false },
        { text: this.$t('country'), value: 'country', sortable: false },
        { text: this.$t('deforestation.areaHa'), value: 'area', sortable: false },
      ];
    },
    historyHeaders() {
      return [
        { text: this.$t('deforestation.uploadHistoryFile'), value: 'originalFileName', sortable: false },
        { text: this.$t('deforestation.uploadHistoryStatus'), value: 'status', sortable: false },
        { text: this.$t('deforestation.uploadHistoryRecords'), value: 'totalRecordsCount', sortable: false },
        { text: this.$t('deforestation.uploadHistoryReused'), value: 'reusedRecordsCount', sortable: false },
        { text: this.$t('deforestation.uploadHistorySkipped'), value: 'failedRecordsCount', sortable: false },
        { text: this.$t('deforestation.uploadHistoryUploadedAt'), value: 'createdAt', sortable: false },
      ];
    },
    farmTotalPages() { return Math.max(1, Math.ceil(this.farmTotal / this.pageSize)); },
    farmFrom() { return this.farmTotal === 0 ? 0 : (this.farmPage - 1) * this.pageSize + 1; },
    farmTo() { return Math.min(this.farmPage * this.pageSize, this.farmTotal); },
    historyTotalPages() { return Math.max(1, Math.ceil(this.historyTotal / this.pageSize)); },
    historyFrom() { return this.historyTotal === 0 ? 0 : (this.historyPage - 1) * this.pageSize + 1; },
    historyTo() { return Math.min(this.historyPage * this.pageSize, this.historyTotal); },
  },
  created() {
    this.onFarmSearch = debounce(() => { this.farmPage = 1; this.loadFarms(); }, 400);
    this.onHistorySearch = debounce(() => { this.historyPage = 1; this.loadHistory(); }, 400);
  },
  mounted() {
    this.loadFarms();
    this.loadHistory();
  },
  methods: {
    hectares(item) {
      const area = item && item.area != null ? Number(item.area) : null;
      if (area == null || isNaN(area)) return '-';
      return (area * 0.404686).toFixed(4);
    },
    statusColor(status) {
      const s = (status || '').toLowerCase();
      if (s === 'completed') return 'success';
      if (s === 'failed') return 'error';
      return 'orange';
    },
    formatDate(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return '-';
      return dt.toLocaleString();
    },
    async loadFarms() {
      const seq = ++this.farmSeq;
      this.farmLoading = true;
      try {
        const res = await DeforestationService.getImportedFarmsLite({
          diligenceReportId: Number(this.diligenceId),
          page: this.farmPage,
          limit: this.pageSize,
          searchPhrase: this.farmSearch || undefined,
        });
        if (seq !== this.farmSeq) return;
        const pp = res && res.data && res.data.productionPlaces;
        this.farms = (pp && pp.rows ? pp.rows : []).map((row) => ({
          id: row.id,
          farmName: (row.farm && row.farm.farmName) || '-',
          farmRef: (row.farm && row.farm.nationalFarmId) || row.farmId || '-',
          country: (row.farm && row.farm.country) || '-',
          area: row.farm && row.farm.area,
        }));
        this.farmTotal = pp && pp.totalCount != null ? pp.totalCount : this.farms.length;
      } catch (e) {
        if (seq === this.farmSeq) { this.farms = []; this.farmTotal = 0; }
      } finally {
        if (seq === this.farmSeq) this.farmLoading = false;
      }
    },
    async loadHistory() {
      const seq = ++this.historySeq;
      this.historyLoading = true;
      try {
        const res = await DeforestationService.getProductionPlaceImportHistory(this.diligenceId, {
          page: this.historyPage,
          limit: this.pageSize,
          search: this.historySearch || undefined,
        });
        if (seq !== this.historySeq) return;
        // postGraphqlQuery resolves (doesn't throw) on GraphQL errors, so check
        // res.errors — otherwise a server failure looks like "no files yet".
        if (res && res.errors && res.errors.length) throw new Error(res.errors[0].message);
        const h = res && res.data && res.data.productionPlaceImportHistory;
        this.history = h && h.rows ? h.rows : [];
        this.historyTotal = h && h.totalCount != null ? h.totalCount : this.history.length;
        this.historyError = '';
      } catch (e) {
        if (seq === this.historySeq) {
          this.history = []; this.historyTotal = 0;
          this.historyError = this.$t('deforestation.uploadHistoryLoadFailed');
          console.error('[ImportedDataPanel] upload history failed:', e && e.message);
        }
      } finally {
        if (seq === this.historySeq) this.historyLoading = false;
      }
    },
    farmPageChange(delta) {
      const next = this.farmPage + delta;
      if (next < 1 || next > this.farmTotalPages) return;
      this.farmPage = next;
      this.loadFarms();
    },
    historyPageChange(delta) {
      const next = this.historyPage + delta;
      if (next < 1 || next > this.historyTotalPages) return;
      this.historyPage = next;
      this.loadHistory();
    },
  },
  beforeDestroy() {
    if (this.onFarmSearch && this.onFarmSearch.cancel) this.onFarmSearch.cancel();
    if (this.onHistorySearch && this.onHistorySearch.cancel) this.onHistorySearch.cancel();
  },
};
</script>

<style scoped>
.imported-data-panel {
  background: #fafafa;
}
</style>
